/// <reference types="google.maps" />
import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { FormAddress, formAddressTypes } from '@shared/models/form-address';
import { Country } from '@shared/models/country';
import { AddressDetails, initialAddressDetails } from '@shared/models/address-details';
import { CountriesService } from '@features/countries/services/countries.service';
import { Observable } from 'rxjs';
import { TradingEntity } from '@shared/types/trading-entity';
import { GroupType } from '@shared/types/group-type';
import { PlacesAutocompleteSelection } from '@eset/eset-shared';
import { Context } from '@shared/types/context';

@Component({
  selector: 'app-address-details',
  templateUrl: './address-details.component.html',
  styleUrls: ['./address-details.component.scss'],
  standalone: false,
})
export class AddressDetailsComponent implements OnInit {
  @Input() selectedCountry: Country;
  @Input() addressDetailsForm: FormGroup;
  @Input() tradingEntity: TradingEntity;
  @Input() formTitle: string;
  @Input() formOptional: boolean;
  @Input() collapsible: boolean;
  @Input() isExpanded: boolean;
  @Input() groupType: GroupType;
  @Input() context?: Context;

  @ViewChild('postalCodeRef') postalCodeRef: any;

  countries$: Observable<Country[]>;
  formAddressTypes = formAddressTypes;
  options: google.maps.places.AutocompleteOptions;

  constructor(private countriesService: CountriesService) {}

  ngOnInit(): void {
    this.countries$ = this.countriesService.getCountriesByTradingEntityAndGroupType(this.tradingEntity, this.groupType);
    this.options = {
      fields: ['address_components', 'name'],
      componentRestrictions: {
        country: this.selectedCountry.attributes.alpha2_code,
      },
    };
  }
  addressChange(address: PlacesAutocompleteSelection) {
    let newBillingDetails: AddressDetails = initialAddressDetails;
    let provinceResult: { longText: string; shortText: string; types: string[] } | undefined;
    const mappedAddress = address.mappedAddress ?? this.mapAddressComponents(address.addressComponents);
    this.formAddressTypes.forEach((addressType: FormAddress) => {
      const result = mappedAddress[addressType.apiType];
      if (!result) return;

      if (addressType.formName === 'province') {
        provinceResult = result;
      } else {
        newBillingDetails = { ...newBillingDetails, [addressType.formName]: result.longText };
      }
    });

    const province = this.selectedCountry?.attributes?.provinces.find(
      (province) =>
        [provinceResult?.longText, provinceResult?.longText?.split(' ').join('-')].includes(province.name) ||
        provinceResult?.shortText === province.code ||
        provinceResult?.longText?.includes(province.name),
    );

    const streetAddress =
      address.streetAddress ||
      [mappedAddress['street_number']?.longText, mappedAddress['route']?.longText]
        .filter((value) => !!value)
        .join(' ')
        .trim();

    this.addressDetailsForm.patchValue({
      city: address.city || newBillingDetails.city,
      province_code: province ? province.code : undefined,
      suburb: address.suburb || newBillingDetails.suburb,
      postal_code: address.postalCode || newBillingDetails.postal_code,
      street_address: streetAddress || address.formattedAddress?.split(',')[0]?.trim() || address.displayName,
    });

    this.addressDetailsForm.markAllAsTouched();
    this.addressDetailsForm.updateValueAndValidity();
  }

  private mapAddressComponents(
    components?: Array<{ longText?: string | null; shortText?: string | null; types?: string[] }>,
  ): Record<string, { longText: string; shortText: string; types: string[] }> {
    const mapped: Record<string, { longText: string; shortText: string; types: string[] }> = {};
    if (!components?.length) return mapped;

    components.forEach((component) => {
      (component.types || []).forEach((type) => {
        if (!mapped[type]) {
          mapped[type] = {
            longText: component.longText || '',
            shortText: component.shortText || '',
            types: component.types || [],
          };
        }
      });
    });

    return mapped;
  }

  countryChange(event: any) {
    this.countriesService.selectCountry(event.target.value);
    if (this.options?.componentRestrictions) {
      this.options.componentRestrictions.country = event.target.value;
    }
    this.addressDetailsForm.patchValue({
      province_code: null,
      city: null,
      suburb: null,
      postal_code: null,
      street_address: null,
    });
    if (event.target.value == 'ZA' && !this.formOptional) {
      this.addressDetailsForm.controls['postal_code'].setValidators([Validators.required]);
      this.addressDetailsForm.controls['postal_code'].updateValueAndValidity();
    } else {
      this.addressDetailsForm.controls['postal_code'].clearValidators();
      this.addressDetailsForm.controls['postal_code'].updateValueAndValidity();
    }

    this.addressDetailsForm.markAllAsTouched();
    Object.keys(this.addressDetailsForm.controls).forEach((controlName) => {
      this.addressDetailsForm.get(controlName)?.markAsDirty();
    });
    this.addressDetailsForm.updateValueAndValidity();
  }

  collapseForm() {
    this.isExpanded = !this.isExpanded;
    return this.isExpanded;
  }
}
